import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
// ==========================================================================
// ✅ ASYNC THUNKS
// ==========================================================================
export const fetchCommissionDailyUpdate = createAsyncThunk("commissionTracker/fetchDailyUpdate", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", "/api/commission-tracker/daily-update", payload, {
            Authorization: token || "",
            "x-module-key": "commission_tracker",
        });
        // ApiCall resolves (not throws) on non-2xx — detect the error shape.
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while fetching the daily update");
        }
        if (response && "data" in response && response.data) {
            const body = response.data;
            const result = body?.data ?? { date: "", month: payload.year_month, summary: [], footer: null };
            // success:false with an empty summary is a valid "no records" response.
            return result;
        }
        return rejectWithValue("Unexpected empty response while fetching the daily update");
    }
    catch (error) {
        console.error("Commission Daily Update API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while fetching the daily update");
    }
});
export const fetchCommissionSummary = createAsyncThunk("commissionTracker/fetchSummary", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", "/api/commission-tracker/summary", payload, {
            Authorization: token || "",
            "x-module-key": "commission_tracker",
        });
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while fetching the summary");
        }
        if (response && "data" in response && response.data) {
            const body = response.data;
            const result = body?.data ?? {
                primary_month: payload.year_month,
                summary: [],
                summaryfooter: null,
            };
            return result;
        }
        return rejectWithValue("Unexpected empty response while fetching the summary");
    }
    catch (error) {
        console.error("Commission Summary API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while fetching the summary");
    }
});
export const fetchCommissionPacer = createAsyncThunk("commissionTracker/fetchPacer", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", "/api/commission-tracker/pacer", payload, {
            Authorization: token || "",
            "x-module-key": "commission_tracker",
        });
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while fetching the pacer");
        }
        if (response && "data" in response && response.data) {
            const body = response.data;
            const result = body?.data ?? {
                month: payload.year_month,
                as_of: "",
                days: [],
                footer: { mtd_commission: 0, commission_in_day: 0, projected_commission_pacer: 0 },
            };
            return result;
        }
        return rejectWithValue("Unexpected empty response while fetching the pacer");
    }
    catch (error) {
        console.error("Commission Pacer API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while fetching the pacer");
    }
});
export const fetchCommissionReport = createAsyncThunk("commissionTracker/fetchReport", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", "/api/commission-tracker", payload, {
            Authorization: token || "",
            "x-module-key": "commission_tracker",
        });
        const errLike = response;
        const status = errLike?.response?.status ?? errLike?.status;
        const serverMessage = errLike?.response?.data?.message ?? errLike?.data?.message;
        if (!response || errLike?.isAxiosError || (typeof status === "number" && status >= 400)) {
            return rejectWithValue(serverMessage || errLike?.message || "An error occurred while fetching the report");
        }
        if (response && "data" in response && response.data) {
            const body = response.data;
            const result = body?.data ?? {
                client: null, channel_type: null, stores: [], selected_store_id: null, report_data: null, last_updated: null,
            };
            return result;
        }
        return rejectWithValue("Unexpected empty response while fetching the report");
    }
    catch (error) {
        console.error("Commission Report API Error:", error);
        return rejectWithValue(error?.response?.data?.message || error?.message || "An error occurred while fetching the report");
    }
});
// ==========================================================================
// ✅ SLICE
// ==========================================================================
const initialState = {
    dailyUpdate: null,
    dailyUpdateLoading: false,
    dailyUpdateError: null,
    lastUpdatedAt: null,
    summary: null,
    summaryLoading: false,
    summaryError: null,
    pacer: null,
    pacerLoading: false,
    pacerError: null,
    report: null,
    reportLoading: false,
    reportError: null,
    reportRequest: null,
};
const commissionTrackerSlice = createSlice({
    name: "commissionTracker",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchCommissionDailyUpdate.pending, (state) => {
            state.dailyUpdateLoading = true;
            state.dailyUpdateError = null;
        })
            .addCase(fetchCommissionDailyUpdate.fulfilled, (state, action) => {
            state.dailyUpdateLoading = false;
            state.dailyUpdate = action.payload;
            state.lastUpdatedAt = action.payload?.last_updated || null;
            state.dailyUpdateError = null;
        })
            .addCase(fetchCommissionDailyUpdate.rejected, (state, action) => {
            state.dailyUpdateLoading = false;
            state.dailyUpdate = null;
            state.dailyUpdateError = action.payload || "Failed to fetch the daily update";
        })
            // ── Summary ──
            .addCase(fetchCommissionSummary.pending, (state) => {
            state.summaryLoading = true;
            state.summaryError = null;
        })
            .addCase(fetchCommissionSummary.fulfilled, (state, action) => {
            state.summaryLoading = false;
            state.summary = action.payload;
            state.summaryError = null;
        })
            .addCase(fetchCommissionSummary.rejected, (state, action) => {
            state.summaryLoading = false;
            state.summary = null;
            state.summaryError = action.payload || "Failed to fetch the summary";
        })
            // ── Pacer ──
            .addCase(fetchCommissionPacer.pending, (state) => {
            state.pacerLoading = true;
            state.pacerError = null;
        })
            .addCase(fetchCommissionPacer.fulfilled, (state, action) => {
            state.pacerLoading = false;
            state.pacer = action.payload;
            state.pacerError = null;
        })
            .addCase(fetchCommissionPacer.rejected, (state, action) => {
            state.pacerLoading = false;
            state.pacer = null;
            state.pacerError = action.payload || "Failed to fetch the pacer";
        })
            // ── Report ──
            .addCase(fetchCommissionReport.pending, (state) => {
            state.reportLoading = true;
            state.reportError = null;
        })
            .addCase(fetchCommissionReport.fulfilled, (state, action) => {
            state.reportLoading = false;
            state.report = action.payload;
            state.reportRequest = action.meta.arg;
            state.reportError = null;
        })
            .addCase(fetchCommissionReport.rejected, (state, action) => {
            state.reportLoading = false;
            state.report = null;
            state.reportRequest = null;
            state.reportError = action.payload || "Failed to fetch the report";
        });
    },
});
// ✅ Selectors
export const selectCommissionDailyUpdate = (state) => state.commissionTracker.dailyUpdate;
export const selectCommissionDailyUpdateLoading = (state) => state.commissionTracker.dailyUpdateLoading;
export const selectCommissionDailyUpdateError = (state) => state.commissionTracker.dailyUpdateError;
export const selectCommissionLastUpdatedAt = (state) => state.commissionTracker.lastUpdatedAt;
export const selectCommissionSummary = (state) => state.commissionTracker.summary;
export const selectCommissionSummaryLoading = (state) => state.commissionTracker.summaryLoading;
export const selectCommissionSummaryError = (state) => state.commissionTracker.summaryError;
export const selectCommissionPacer = (state) => state.commissionTracker.pacer;
export const selectCommissionPacerLoading = (state) => state.commissionTracker.pacerLoading;
export const selectCommissionPacerError = (state) => state.commissionTracker.pacerError;
export const selectCommissionReport = (state) => state.commissionTracker.report;
export const selectCommissionReportLoading = (state) => state.commissionTracker.reportLoading;
export const selectCommissionReportError = (state) => state.commissionTracker.reportError;
export const selectCommissionReportRequest = (state) => state.commissionTracker.reportRequest;
export default commissionTrackerSlice.reducer;
